/**
 * Générateur — Automatisme : écrire 3 × x sous la forme 3x et savoir que 3x
 * c'est 3 × x.
 * Attendu ID : 1108 | Niveau : 4ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 10, ligne 708 :
 * « Écrire 3 × x sous la forme 3x et savoir que 3x c'est 3 × x. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12 ; arbitrage
 * prof le même jour) ────────────────────────────────────────────────────────
 * La convention comme fait nu, en ÉCRITURE PURE, dans les deux sens : écrire
 * (3 × x → 3x ; x × 3 → 3x : mise en œuvre Eduscol 2026 p. 13, « 2 × n
 * s'écrit 2n ») et lire (3x → 3 × x, 3x = x + x + x). La SUBSTITUTION
 * (« 3x pour x = 8, c'est… », l'item DEPP 4e 2023 q.17) est un test
 * d'égalité : elle vit dans 1111, pas ici (arbitrage prof : la première
 * version de ce gen la portait en QCM, en flash et au Vrai/Faux, tout est
 * parti dans 1111 avec le sacrifié). Le monôme seul, jamais « 1 + 3x ».
 * Coefficient 2..9 (a = 1 est la règle de 1109) ; deux lettres (a × b = ab)
 * et coefficient décimal : non (À ARBITRER).
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NE2 « Réduire
 * un produit » options 0-1 (a 1..9), même entrée que 1109.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes en
 *     texte, un nombre à saisir : « 3 × x = …x » ; « x × 5 = …x » ;
 *     « 7x = … × x » ; « 4x = x + x + x + x » lu en nombre de termes
 *     (« 4x = … termes de x »). Le sens lecture sans calcul.
 *   Carte 2 « 3 × x s'écrit… » (ecriture_qcm_unique) : QUATRE écritures :
 *     3x (clé), x3 (ordre_inverse : lettre devant), 3 + x
 *     (lecture_additive), x³ (puissance) ; pur rappel (doctrine, règle 9),
 *     sans fiche propre.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes parmi cinq
 *     gabarits (« 3x = 3 × x » V ; « 3x = 3 + x » F ; « x × 3 = 3x » V ;
 *     « 3x = x + x + x » V ; « 2x = x × x » F), une à trois vraies.
 *
 * INVARIANTS : coefficient a de 2 à 9, lettres x, n, t, y, a ; jamais de
 * nombre substitué à la lettre.
 * SACRIFICE : aucun, et PAS DE FICHE (seconde passe 2026-09-12) : la
 * connaissance EST la réponse (« 3x, c'est 3 × x », doctrine règle 9) ; le
 * sens vit dans la fiche 1109 (paquets de x), la valeur dans celle de 1111.
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   lecture_additive : 3x lu 3 + x (distracteur « a + v » de 933) ;
 *   puissance        : 3x lu x³ (nom posé en 1103) ;
 *   ordre_inverse    : x3, la lettre devant le nombre (inférence, la
 *                      convention elle-même) ;
 *   concatenation    : partie dans 1111 avec la substitution (DEPP q.17).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1108;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = '3 × x s\'écrit…';
const L_VF = 'Vrai ou faux';

const FOIS = '\\times';
const LETTRES = ['x', 'x', 'x', 'n', 't', 'y', 'a'];

// ─── Le domaine ───────────────────────────────────────────────────────────

const verifierCoef = (a) => {
    if (!Number.isInteger(a) || a < 2 || a > 9) throw new Error(`gen_1108 : coefficient hors domaine ${a}`);
    return a;
};
const tirerCoef = () => verifierCoef(randInt(2, 9));

const EXPLICATIONS = {
    lecture_additive: '3x, c\'est 3 × x, pas 3 + x.',
    puissance: '3x, c\'est trois fois x (x + x + x), pas x × x × x.',
    ordre_inverse: 'Le nombre s\'écrit devant la lettre : 3 × x s\'écrit 3x.',
    regle: 'Un nombre collé à une lettre, c\'est une multiplication : 3x = 3 × x = x + x + x.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille (lettre_devant_le_nombre proposé) ; la série flash n'en range pas : ses trous recopient l'énoncé. 3x lu x³ (puissance) a son propre code depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md). */
const CODE_COMMUN = { lecture_additive: 'lecture_additive', puissance: 'coefficient_lu_comme_exposant', ordre_inverse: 'lettre_devant_le_nombre' };

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

// ─── Carte 1 : Série flash (KaTeX, un nombre par ligne) ───────────────────
// Tout en mode maths, lignes entières $…$ (règle prof 2026-09-12 : la lettre
// s'affiche en italique de formule, jamais en texte, même dans un trou).

const genFlash = () => {
    const L = randomChoice(LETTRES);
    const coefs = shuffle([2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 4).map(verifierCoef);
    const n = Math.min(coefs[3], 5); // la somme itérée reste lisible : 5 termes au plus
    const lignes = [
        `$${coefs[0]} ${FOIS} ${L} = {{${coefs[0]}}}${L}$`,
        `$${L} ${FOIS} ${coefs[1]} = {{${coefs[1]}}}${L}$`,
        `$${coefs[2]}${L} = {{${coefs[2]}}} ${FOIS} ${L}$`,
        `$${Array(n).fill(L).join(' + ')} = {{${n}}}${L}$`,
    ];
    const payload = makeTexteTrou(lignes.join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { lettre: L, coefs: [coefs[0], coefs[1], coefs[2], n] };
    return payload;
};

// ─── Carte 2 : QCM « 3 × x s'écrit… » ─────────────────────────────────────

const genQcm = () => {
    const L = randomChoice(LETTRES);
    const a = tirerCoef();
    const lettreDevant = randomChoice([true, false]);
    const enonce = lettreDevant ? `${L} ${FOIS} ${a}` : `${a} ${FOIS} ${L}`;
    const bon = tex(`${a}${L}`);
    const faux = [
        { texte: tex(`${L}${a}`), code: 'ordre_inverse' },
        { texte: tex(`${a} + ${L}`), code: 'lecture_additive' },
        { texte: tex(`${L}^${a}`), code: 'puissance' },
    ];
    const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, `<p><b>${tex(enonce)} s'écrit…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, faux.map((f) => ({ code: CODE_COMMUN[f.code], valeur: f.texte })));
    payload._meta.params = { lettre: L, a, lettreDevant, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 3 : Vrai ou faux (4 lignes) ────────────────────────────────────

const GABARITS = {
    produit_vrai: (L) => { const a = tirerCoef(); return { ligne: tex(`${a}${L} = ${a} ${FOIS} ${L}`), vrai: true }; },
    somme_fausse: (L) => { const a = tirerCoef(); return { ligne: tex(`${a}${L} = ${a} + ${L}`), vrai: false, code: 'lecture_additive' }; },
    commutee_vraie: (L) => { const a = tirerCoef(); return { ligne: tex(`${L} ${FOIS} ${a} = ${a}${L}`), vrai: true }; },
    iteree_vraie: (L) => { const a = randInt(2, 4); return { ligne: tex(`${a}${L} = ${Array(a).fill(L).join(' + ')}`), vrai: true }; },
    puissance_fausse: (L) => { const a = randInt(2, 3); return { ligne: tex(`${a}${L} = ${Array(a).fill(L).join(` ${FOIS} `)}`), vrai: false, code: 'puissance' }; },
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const noms = shuffle(Object.keys(GABARITS)).slice(0, 4);
        const items = noms.map((nom) => ({ nom, ...GABARITS[nom](L) }));
        const nbVraies = items.filter((it) => it.vrai).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        if (new Set(items.map((it) => it.ligne)).size < 4) continue;
        const payload = makeVraiFaux(items.map((it) => it.ligne), items.map((it) => (it.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, items.filter((it) => it.code).map((it) => ({ code: CODE_COMMUN[it.code], ligne: it.ligne })));
        payload._meta.params = { lettre: L, items: items.map(({ nom, vrai, code }) => ({ nom, vrai, ...(code ? { code } : {}) })) };
        return payload;
    }
    const lignes = [tex('3x = 3 \\times x'), tex('3x = 3 + x'), tex('x \\times 3 = 3x'), tex('2x = x \\times x')];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, [{ code: CODE_COMMUN.lecture_additive, ligne: lignes[1] }, { code: CODE_COMMUN.puissance, ligne: lignes[3] }]);
    payload._meta.params = { lettre: 'x', items: [{ nom: 'produit_vrai', vrai: true }, { nom: 'somme_fausse', vrai: false, code: 'lecture_additive' }, { nom: 'commutee_vraie', vrai: true }, { nom: 'puissance_fausse', vrai: false, code: 'puissance' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre écritures distinctes ; la clé est la convention (nombre devant, collé) ; pur rappel, sans fiche propre',
        'aucun nombre substitué à la lettre : la substitution est 1111 (arbitrage prof 2026-09-12)',
    ],
    qcm_unique: {
        support: '« a × x s\'écrit… » ou « x × a s\'écrit… », a de 2 à 9',
        familles: {
            ordre_inverse: 'xa : la lettre devant le nombre',
            lecture_additive: 'a + x (« a + v » de 933)',
            puissance: 'x^a : le coefficient pris pour un exposant',
        },
    },
    vf_tableau_vf: 'quatre lignes parmi cinq gabarits (produit, somme fausse, commutée, addition itérée, puissance fausse), une à trois vraies',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    ecriture_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'ecriture', label: L_QCM, etapes: 1, formatsList: [{ key: 'ecriture_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
